// pages/secure/MailboxPage.js
// The office mailroom: compose and send real email from your desk, and review
// what you've sent. Managing brokers see the whole office's outbound mail.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

const STATUS_BADGE = {
    sent: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    queued: 'bg-amber-100 text-amber-700',
};

const EMPTY_FORM = { to: '', cc: '', subject: '', body_html: '' };

const MailboxPage = () => {
    const apiClient = useApiClient();
    const [configured, setConfigured] = useState(null);
    const [messages, setMessages] = useState([]);
    const [viewing, setViewing] = useState(null);
    const [isComposing, setIsComposing] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState('');
    const [sentFlash, setSentFlash] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const load = useCallback(() => {
        setIsLoading(true);
        Promise.all([
            apiClient.get('/api/mail/status'),
            apiClient.get('/api/mail'),
        ])
            .then(([status, mail]) => {
                setConfigured(status.configured);
                setMessages(mail);
            })
            .catch(err => setError(err.message || 'Failed to load the mailroom.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    useEffect(() => { load(); }, [load]);

    const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSend = async (e) => {
        e.preventDefault();
        setIsSending(true);
        setError('');
        const payload = {
            to: form.to,
            cc: form.cc || undefined,
            subject: form.subject,
            // Preserve line breaks from the plain-text compose box
            body_html: `<div style="font-family:sans-serif; white-space:pre-wrap;">${form.body_html
                .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>`,
        };
        // Checked, not blocked (owner decision O4): anyone on the do-not-contact list is
        // named, with the date they opted out, and the sender must confirm to go ahead.
        // The send is then flagged for the managing broker's review.
        const confirmOptOuts = (list) => window.confirm(
            `${list.map(o => `${o.address} opted out${o.opted_out_on ? ` on ${o.opted_out_on}` : ''}`).join('\n')}\n\n`
            + 'Send anyway? The send will be recorded for the managing broker\'s review.');
        try {
            const check = await apiClient.get(`/api/mail/opt-out-check?to=${encodeURIComponent([form.to, form.cc].filter(Boolean).join(','))}`);
            if (check.opted_out && check.opted_out.length) {
                if (!confirmOptOuts(check.opted_out)) { setIsSending(false); return; }
                payload.confirm_opted_out = true;
            }
            try {
                await apiClient.post('/api/mail/send', payload);
            } catch (err) {
                // Someone opted out between the check and the send: ask again.
                if (err.status !== 409 || !err.payload?.opted_out || !confirmOptOuts(err.payload.opted_out)) throw err;
                await apiClient.post('/api/mail/send', { ...payload, confirm_opted_out: true });
            }
            setForm(EMPTY_FORM);
            setIsComposing(false);
            setSentFlash(true);
            setTimeout(() => setSentFlash(false), 3000);
            load();
        } catch (err) {
            setError(err.message || 'The email could not be sent.');
        } finally {
            setIsSending(false);
        }
    };

    const openMessage = async (id) => {
        try {
            setViewing(await apiClient.get(`/api/mail/${id}`));
        } catch (err) {
            setError(err.message || 'Failed to open the message.');
        }
    };

    return (
        <div className="p-6 max-w-5xl">
            <div className="flex items-center justify-between mb-1">
                <h1 className="text-2xl font-bold text-slate-800">Mailroom</h1>
                <button onClick={() => { setIsComposing(v => !v); setError(''); }}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                        disabled={configured === false}>
                    {isComposing ? 'Close' : '✉ Compose'}
                </button>
            </div>
            <p className="text-slate-500 mb-6">Send email from your desk — every message is kept on file.</p>

            {configured === false && (
                <div className="mb-4 border border-amber-300 bg-amber-50 text-amber-800 rounded-md p-4">
                    Email sending isn't switched on yet — the Microsoft 365 connection is pending.
                    Ask the managing broker to complete the setup.
                </div>
            )}
            {error && <p className="mb-4 text-red-500">{error}</p>}
            {sentFlash && <p className="mb-4 text-green-600">✓ Email sent.</p>}

            {isComposing && (
                <form onSubmit={handleSend} className="bg-white rounded-lg shadow p-6 mb-6 space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-slate-700">To (comma-separate multiple)</label>
                        <input name="to" value={form.to} onChange={handleChange} required
                               placeholder="client@example.com" className="mt-1 w-full border rounded-md px-3 py-2" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700">Cc</label>
                        <input name="cc" value={form.cc} onChange={handleChange}
                               className="mt-1 w-full border rounded-md px-3 py-2" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700">Subject</label>
                        <input name="subject" value={form.subject} onChange={handleChange} required
                               className="mt-1 w-full border rounded-md px-3 py-2" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700">Message</label>
                        <textarea name="body_html" value={form.body_html} onChange={handleChange} rows="8" required
                                  className="mt-1 w-full border rounded-md px-3 py-2" />
                    </div>
                    <button type="submit" disabled={isSending || configured === false}
                            className="bg-blue-600 text-white px-5 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                        {isSending ? 'Sending...' : 'Send'}
                    </button>
                </form>
            )}

            {/* Sent mail */}
            <div className="bg-white rounded-lg shadow divide-y">
                {isLoading && <p className="p-4">Checking the mailroom...</p>}
                {!isLoading && messages.length === 0 && <p className="p-4 text-slate-500">No mail sent yet.</p>}
                {messages.map(m => (
                    <button key={m.message_id} onClick={() => openMessage(m.message_id)}
                            className="w-full text-left p-3 hover:bg-slate-50 flex items-center gap-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${STATUS_BADGE[m.status] || ''}`}>{m.status}</span>
                        <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-700 truncate">{m.subject}</p>
                            <p className="text-xs text-slate-500 truncate">
                                To: {m.to_addresses} · {new Date(m.created_at).toLocaleString()}
                                {m.sender_username ? ` · by ${m.sender_username}` : ''}
                            </p>
                        </div>
                    </button>
                ))}
            </div>

            {/* Message viewer */}
            {viewing && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setViewing(null)}>
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto"
                         onClick={e => e.stopPropagation()}>
                        <div className="flex items-start justify-between mb-3">
                            <h2 className="text-lg font-semibold text-slate-800">{viewing.subject}</h2>
                            <button onClick={() => setViewing(null)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
                        </div>
                        <div className="text-sm text-slate-600 space-y-0.5 mb-4">
                            <p><span className="font-medium">From:</span> {viewing.from_address}</p>
                            <p><span className="font-medium">To:</span> {viewing.to_addresses}</p>
                            {viewing.cc_addresses && <p><span className="font-medium">Cc:</span> {viewing.cc_addresses}</p>}
                            <p><span className="font-medium">Status:</span> {viewing.status}
                               {viewing.sent_at ? ` at ${new Date(viewing.sent_at).toLocaleString()}` : ''}</p>
                            {viewing.error && <p className="text-red-600"><span className="font-medium">Error:</span> {viewing.error}</p>}
                        </div>
                        <div className="border-t pt-4 prose prose-sm max-w-none"
                             dangerouslySetInnerHTML={{ __html: viewing.body_html }} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default MailboxPage;
